import { Link } from "react-router-dom";
import { ArrowRight, Megaphone } from "lucide-react";
import { SIGN_IN_ACTIVITY_CUTOFF_LABEL } from "../consts";

interface RegistrationClosedBannerProps {
    // When set, the banner ends with a login CTA (used on the sign-up page).
    signInHref?: string;
}

// Full-width announcement strip shown directly under the navbar on the auth
// pages while the product is winding down. Rendered unconditionally -- unlike
// the sign-in enforcement it is not gated on REGISTRATION_ENABLED, so it is
// also visible on the test project.
//
// Layout: stacked and centered on mobile, icon | text | CTA in a row from sm up.
const RegistrationClosedBanner = ({ signInHref }: RegistrationClosedBannerProps) => (
    <div
        role="status"
        data-testid="registration-closed-banner"
        className="w-full border-b border-secondary-200 bg-gradient-to-r from-secondary-200/50 via-secondary-100 to-secondary-200/50 shadow-sm"
    >
        <div className="mx-auto flex max-w-975 flex-col items-center gap-3 px-4 py-4 text-center sm:flex-row sm:gap-4 sm:px-6 sm:py-3.5 sm:text-left">
            <span
                aria-hidden="true"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-secondary shadow-sm ring-1 ring-secondary-200"
            >
                <Megaphone className="h-5 w-5" />
            </span>

            <div className="min-w-0 flex-1">
                <p className="font-poppins text-sm font-semibold leading-snug text-primary sm:text-base">
                    Derzeit keine neuen Registrierungen
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-primary/70 sm:text-sm">
                    Wir nehmen aktuell keine neuen Konten mehr an. Mit einem
                    bestehenden Konto kannst Du Dich weiterhin anmelden, sofern es
                    seit dem{" "}
                    <span className="whitespace-nowrap font-medium text-primary">
                        {SIGN_IN_ACTIVITY_CUTOFF_LABEL}
                    </span>{" "}
                    aktiv war.
                </p>
            </div>

            {signInHref && (
                <Link
                    to={signInHref}
                    className="inline-flex w-full shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-secondary px-5 py-2 font-Outfit text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-secondary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-400 focus-visible:ring-offset-2 sm:w-auto"
                >
                    Zum Login
                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                </Link>
            )}
        </div>
    </div>
);

export default RegistrationClosedBanner;
